import { getToken, isValidAccessToken, removeToken } from '@/utils/auth'
import { setCookie, getCookie, clearCookie, urlParamsToObject } from '@/utils/tools'
import { createLoginSign } from '@/api/system'
import { useSystemStore } from '@/stores/system'
import { useUserStore } from '@/stores/user'
import { usePermissionStore } from '@/stores/permission'
import { useLayoutStore } from '@/stores/layout'
import signalrNotify from '@/utils/signalR'

const whiteList = [
  '/login',
  '/redirect',
  '/loginAuto',
  '/clientAutoLogin',
  '/oidc-callback',
  '/404',
  '/500',
  '/welcome',
  '/forgetPassword',
  '/moodle',
  '/retrieveEmail',
  '/tutorClient'
]

function addRouteSafe(router, route) {
  if (route.name && router.hasRoute(route.name)) return
  router.addRoute(route)
}

export function ensureAsyncRoutes(router, permissionStore, roles) {
  const missing =
    !router.hasRoute("admin") ||
    !router.hasRoute("classTab") ||
    !router.hasRoute("classDetail")
  if (!missing) return false

  ;["admin", "growthCenterIndex", "catchAll"].forEach((name) => {
    if (router.hasRoute(name)) router.removeRoute(name)
  })

  const routes = permissionStore.generateRoutes(roles?.length ? roles : ["admin"])
  routes.forEach((r) => addRouteSafe(router, r))
  permissionStore.routesAdded = true
  return true
}

export function setupPermissionGuard(router) {
  router.beforeEach(async (to, _from, next) => {
    const systemStore = useSystemStore()
    const userStore = useUserStore()
    const permissionStore = usePermissionStore()
    const layoutStore = useLayoutStore()

    await systemStore.initSystemSetting()

    let token = getToken()
    let redirectUrl = ''

    try {
      const href = location.href

      if (href.includes('?')) {
        const para = urlParamsToObject(href.substring(href.indexOf('?') + 1))
        if (para.client === 'tutorclient') {
          setCookie('client', para.client, 100)
        }
      }

      if (href.includes('#')) {
        const paraStr = href.substring(href.indexOf('#') + 1)
        const para = urlParamsToObject(paraStr)
        redirectUrl = para.redirectUrl || ''
        userStore.setAuthStr(paraStr)
        if (Object.prototype.hasOwnProperty.call(para, 'access_token')) {
          if (isValidAccessToken(para.access_token)) {
            token = userStore.loginThree(para)
          } else {
            // e.g. #access_token=null → clear and force SSO login
            userStore.fedLogOut()
            removeToken()
            token = ''
          }
        }
        if (para.client === 'tutorclient') {
          setCookie('client', para.client, 100)
        }
      }

      if (to.path === '/oidc-callback') {
        if (getCookie('client') === 'tutorclient') {
          createLoginSign()
            .then((res) => {
              if (res.data?.ErrorCode === 0) {
                setCookie('loginSign', res.data.Msg, 30)
              }
            })
            .catch(() => {})
          // Vue2: 把 TUID/Token 回传桌面壳（若客户端已注入 GetTeachUserData）
          userStore.notifyTutorClientUserData()
        }
        next({ path: '/home' })
        return
      }

      if (to.path === '/redirect') {
        // No valid token → welcome（与 Vue2 未登录落地一致）
        if (!isValidAccessToken(token) && !getToken()) {
          next({ path: '/welcome', replace: true })
          return
        }
        next({ path: redirectUrl || '/home' })
        return
      }

      // Multi-host menu redirect — only when logged in AND current host is in
      // Hosts.WhiteList with redirect===1. Unknown hosts (e.g. tutornew.dev before
      // whitelist entry) must stay put — otherwise SSO lands on main site
      // (often misconfigured as tutorapi.dev.acasdoc.com) with #access_token=...
      if (isValidAccessToken(token)) {
        const _whiteList = layoutStore.whiteList
        const currentOrigin = `${location.protocol}//${location.host}`
        const _currDomain = _whiteList.find((t) => t.domain === currentOrigin)
        if (_currDomain && _currDomain.redirect === 1) {
          const rexHost = /http[s]?:\/\/(.+?)\//
          const rexRoute = /\/(\w+)\/?/
          const routeParent =
            (rexRoute.test(to.path) && to.path.match(rexRoute)[1]) || to.path
          const menu = layoutStore.mainMenus.find((e) => e.routeName === routeParent)
          if (
            menu?.url &&
            rexHost.test(menu.url) &&
            menu.url.match(rexHost)[1] !== location.host
          ) {
            const host = menu.url.match(/(http[s]?:\/\/.+?)\//)[1]
            const clientQs =
              getCookie('client') === 'tutorclient' ? '&client=tutorclient' : ''
            location.href = `${host}/redirect#access_token=${token}&redirectUrl=${to.path}${clientQs}`
            return
          }
          if (_currDomain.level === 1) {
            // ok
          } else if (menu && (!menu.url || !rexHost.test(menu.url))) {
            const _mainDomain = _whiteList.find((t) => t.level === 1)
            if (_mainDomain && _mainDomain.domain !== currentOrigin) {
              const clientQs =
                getCookie('client') === 'tutorclient' ? '&client=tutorclient' : ''
              location.href = `${_mainDomain.domain}/redirect#access_token=${token}&redirectUrl=${to.path}${clientQs}`
              return
            }
          }
        }
      }
    } catch (error) {
      console.log('permission error', error)
    }

    token = getToken() || token
    if (isValidAccessToken(token)) {
      if (to.path === '/welcome' || to.path === '/login') {
        next({ path: '/home' })
        return
      }
      if (to.path === '/404' || to.path === '/500') {
        next()
        return
      }

      if (userStore.roles.length === 0) {
        try {
          const data = await userStore.fetchUserInfo()
          userStore.fetchUserMoreInfo()
          // Vue2: fire-and-forget growth system bootstraps after GetUserInfo
          userStore.fetchTutorHomeTask()
          userStore.fetchGradeHonorDetail()
          userStore.fetchPetInfo()
          userStore.fetchMilestoneList()
          userStore.fetchGrowthSystemIsFirstLogin()
          // Vue2: store.dispatch('GetTutorTrail') — Enrolment Tol count
          userStore.fetchTutorTrail()

          // Vue2 permission.js: GetUserInfo 成功后 signalrNotify.start()
          signalrNotify.start()

          ensureAsyncRoutes(router, permissionStore, data.roles)
          // Must keep query/hash: { path: to.fullPath } drops ?ID=... (Vue Router path ≠ fullPath)
          next({ path: to.path, query: to.query, hash: to.hash, replace: true })
        } catch (err) {
          console.log('GetUserInfo failed', err)
          signalrNotify.stop()
          userStore.fedLogOut()
          permissionStore.reset()
          let retryTimes = Number(getCookie('retryTimes') || 0)
          if (retryTimes < 3) {
            setCookie('retryTimes', String(++retryTimes))
            // Vue2: next({ path: '/welcome' }) — 避免 TutorClient 卡在近空白 /login
            next({ path: '/welcome', replace: true })
          } else {
            clearCookie('retryTimes')
            location.href = '/500'
          }
        }
        return
      }

      // HMR / router recreate can drop async routes while Pinia roles remain.
      if (ensureAsyncRoutes(router, permissionStore, userStore.roles)) {
        next({ path: to.path, query: to.query, hash: to.hash, replace: true })
        return
      }

      next()
      return
    }

    // Not logged in: whitelist pages stay; others → /welcome（与 Vue2 一致，客户端可点 Login）
    if (whiteList.includes(to.path)) {
      next()
    } else {
      next({ path: '/welcome', replace: true })
    }
  })
}
